import {
  formatBrandMoney,
  getBrandLocale,
  getBrandDatePickerLocale,
} from '../../../shared/utils/brandCurrency';
import {
  getBrandPhonePlaceholder,
  normalizeBrandPhone,
  SAUDI_PHONE_PREFIX,
} from '../../../shared/utils/brandCountry';
import { isSaudiBrand } from '../../../products/saudi/utils/isSaudiBrand';

/** Saudi business week: Sun(0)–Thu(4); weekend Fri(5)–Sat(6). */
export const SAUDI_WEEKEND_DAYS = [5, 6];

/** Default Saudi office hours (Sun–Thu). */
export const SAUDI_BUSINESS_HOURS = {
  start: '09:00',
  end: '17:00',
  days: [0, 1, 2, 3, 4],
};

/** Common Saudi CRM communication channels (no Algerian-specific channels). */
export const SAUDI_COMM_CHANNELS = [
  { value: 'whatsapp', labelKey: 'channel_whatsapp' },
  { value: 'phone', labelKey: 'channel_phone' },
  { value: 'email', labelKey: 'channel_email' },
  { value: 'sms', labelKey: 'channel_sms' },
  { value: 'meeting', labelKey: 'channel_meeting' },
  { value: 'video', labelKey: 'channel_video' },
];

export const SAUDI_MEETING_TYPES = [
  { value: 'in_person', labelKey: 'meeting_in_person' },
  { value: 'video', labelKey: 'meeting_video' },
  { value: 'phone', labelKey: 'meeting_phone' },
];

/** Saudi public holidays often referenced in CRM reminders (Gregorian + key Islamic). */
export const SAUDI_HOLIDAY_HINTS = [
  'اليوم الوطني السعودي (23 سبتمبر)',
  'يوم التأسيس (22 فبراير)',
  'عيد الفطر',
  'عيد الأضحى',
];

export function getCrmDateLocale(language) {
  return getBrandLocale(language);
}

export function getCrmDatePickerLocale() {
  return getBrandDatePickerLocale();
}

export function formatCrmDate(value, language, options = {}) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const locale = getCrmDateLocale(language);
  const fmt = options.dateStyle
    ? { dateStyle: options.dateStyle, timeStyle: options.timeStyle }
    : {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...(options.includeTime
          ? { hour: '2-digit', minute: '2-digit', hour12: true }
          : {}),
      };
  return d.toLocaleString(locale, fmt);
}

export function formatCrmMoney(amount) {
  return formatBrandMoney(amount);
}

export function formatCrmPhone(value) {
  const normalized = normalizeBrandPhone(value);
  if (!normalized) return '—';
  if (isSaudiBrand() && normalized.startsWith(SAUDI_PHONE_PREFIX)) {
    const digits = normalized.replace(/\D/g, '').slice(3);
    if (digits.length >= 9) {
      return `${SAUDI_PHONE_PREFIX} ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 9)}`.trim();
    }
  }
  return normalized;
}

export function getCrmPhoneTelHref(value) {
  const normalized = normalizeBrandPhone(value);
  if (!normalized) return null;
  return `tel:${normalized.replace(/\s/g, '')}`;
}

export function getCrmWhatsAppHref(value) {
  const normalized = normalizeBrandPhone(value);
  if (!normalized) return null;
  const digits = normalized.replace(/\D/g, '');
  return `https://wa.me/${digits}`;
}

export function getCrmPhonePlaceholder() {
  return getBrandPhonePlaceholder();
}

export function isSaudiWeekend(date) {
  const d = date instanceof Date ? date : new Date(date);
  return SAUDI_WEEKEND_DAYS.includes(d.getDay());
}

export function isWithinSaudiBusinessHours(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (!SAUDI_BUSINESS_HOURS.days.includes(d.getDay())) return false;
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const total = hours * 60 + minutes;
  const [startH, startM] = SAUDI_BUSINESS_HOURS.start.split(':').map(Number);
  const [endH, endM] = SAUDI_BUSINESS_HOURS.end.split(':').map(Number);
  return total >= startH * 60 + startM && total <= endH * 60 + endM;
}

/** Prayer-time buffer note for scheduling (approx. midday break). */
export function getPrayerTimeNote(language) {
  if (!isSaudiBrand()) return null;
  return language?.startsWith('ar')
    ? 'يُفضّل تجنب المواعيد حول أوقات الصلاة (خصوصاً الظهر).'
    : null;
}
